import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { AdminClinic } from "../api/contracts";
import {
  ActionsColumnHeader,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
  StatusPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  resolveTherapistNames,
  useAdminDirectory,
} from "../hooks/useAdminDirectory";
import { PortalLink } from "../components/PortalLink";
import { RowActionMenu } from "../components/HomeworkControls";

function EmptyValue() {
  return <span className="cp-empty-value">—</span>;
}

interface ClinicDirectoryRow extends AdminClinic {
  therapistNames: string[];
}

function clinicColumns(
  clinicianCode: string,
): readonly PortalTableColumn<ClinicDirectoryRow>[] {
  return [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (clinic) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          editPath={`/clinician-portal/admin/clinics/${encodeURIComponent(clinic.id)}/edit`}
          activityName={clinic.clinicName || "clinic"}
          canEdit
          showDuplicate={false}
          showDelete={false}
        />
      ),
    },
    {
      key: "clinicName",
      header: "Clinic Name",
      initialWidth: 360,
      minWidth: 220,
      render: (clinic) => clinic.clinicName || <EmptyValue />,
    },
    {
      key: "therapists",
      header: "Therapists",
      initialWidth: 360,
      minWidth: 220,
      render: (clinic) => (
        <RelationshipPillList names={clinic.therapistNames} />
      ),
    },
    {
      key: "status",
      header: "Status",
      initialWidth: 160,
      minWidth: 120,
      resizable: false,
      render: (clinic) =>
        clinic.status ? <StatusPill status={clinic.status} /> : <EmptyValue />,
    },
  ];
}

export function ClinicsPage({ clinicianCode }: { clinicianCode: string }) {
  const [search, setSearch] = useState("");
  const state = useAdminDirectory(clinicianCode);
  const directory =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data = directory?.clinics;
  const therapistData = directory?.therapists;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null &&
      therapistData?.found &&
      therapistData.authorized &&
      therapistData.therapist?.role === "admin" &&
      therapistData.patient === null,
  );
  const rows = useMemo<ClinicDirectoryRow[]>(
    () =>
      data && therapistData
        ? data.clinics.map((clinic) => ({
            ...clinic,
            therapistNames: resolveTherapistNames(
              clinic,
              therapistData.therapists,
            ),
          }))
        : [],
    [data, therapistData],
  );
  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return rows;

    return rows.filter((clinic) =>
      [clinic.clinicName, ...clinic.therapistNames]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [rows, search]);
  const resultCount = search.trim()
    ? `${filteredRows.length} of ${rows.length} ${
        rows.length === 1 ? "clinic" : "clinics"
      }`
    : `${rows.length} ${rows.length === 1 ? "clinic" : "clinics"}`;
  const columns = useMemo(() => clinicColumns(clinicianCode), [clinicianCode]);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clinics"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Clinics"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Administration" }, { label: "Clinics" }]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {state.status === "loading" ? (
          <LoadingState label="Loading Clinics" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Clinics are unavailable")} />
        ) : null}
        {available && data ? (
          <section aria-label="Clinics list">
            <div className="cp-content-header">
              <div>
                <p>Review clinics and their assigned clinicians.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div className="cp-list-toolbar">
              <div className="cp-list-toolbar__filters">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">Search clinics</span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search clinics"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </label>
              </div>
              <div className="cp-list-toolbar__actions">
                <PortalLink
                  className="cp-button cp-button--primary"
                  path="/clinician-portal/admin/clinics/new"
                  clinicianCode={clinicianCode}
                >
                  Add Clinic
                </PortalLink>
              </div>
            </div>
            {filteredRows.length ? (
              <PortalDataTable
                caption="Clinics"
                columns={columns}
                records={filteredRows}
                getRecordKey={(clinic) => clinic.id}
              />
            ) : rows.length ? (
              <EmptyState
                title="No clinics match this search."
                description="Try a different clinic or clinician name."
              />
            ) : (
              <EmptyState
                title="No clinics found."
                description="No clinics are currently available."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
